import { imageManager, ImageFile } from './imageManager'
import { sharepointImageService, SharePointImageFile } from './sharepointImageService'

export interface UnifiedImageFile {
  id: string
  name: string
  dataUrl: string
  source: 'local' | 'sharepoint'
  category: string
}

class UnifiedImageService {
  // Get image by ID from both sources
  async getImageById(imageId: string): Promise<UnifiedImageFile | null> {
    // Try local first
    const localImages = imageManager.getAllImages()
    const localImage = localImages.find(img => img.id === imageId)
    
    if (localImage && localImage.dataUrl) {
      return {
        id: localImage.id,
        name: localImage.name,
        dataUrl: localImage.dataUrl,
        source: 'local',
        category: localImage.category
      }
    }

    // Try SharePoint cache (both selected and all images)
    try {
      const allSharePointImages = await sharepointImageService.getImagesWithLocalInfo()
      const sharePointImage = allSharePointImages.find(img => img.id === imageId)
      
      if (sharePointImage && (sharePointImage.localDataUrl || sharePointImage.downloadUrl)) {
        return {
          id: sharePointImage.id,
          name: sharePointImage.name,
          dataUrl: sharePointImage.localDataUrl || sharePointImage.downloadUrl,
          source: 'sharepoint',
          category: sharePointImage.category
        }
      }
    } catch (error) {
      console.warn('Failed to load SharePoint images:', error)
    }

    return null
  }

  // Get all images by category from both sources
  async getImagesByCategory(category: string): Promise<UnifiedImageFile[]> {
    const unifiedImages: UnifiedImageFile[] = []

    // Get local images
    const localImages = imageManager.getImagesByCategory(category)
    localImages.forEach(img => {
      if (img.dataUrl) {
        unifiedImages.push({
          id: img.id,
          name: img.name,
          dataUrl: img.dataUrl,
          source: 'local',
          category: img.category
        })
      }
    })

    // Get SharePoint images (both selected and all available images)
    try {
      const allSharePointImages = await sharepointImageService.getImagesWithLocalInfo()
      const categorySharePointImages = allSharePointImages.filter(img => img.category === category)
      
      categorySharePointImages.forEach(img => {
        if (img.localDataUrl || img.downloadUrl) {
          unifiedImages.push({
            id: img.id,
            name: img.name,
            dataUrl: img.localDataUrl || img.downloadUrl,
            source: 'sharepoint',
            category: img.category
          })
        }
      })
    } catch (error) {
      console.warn('Failed to load SharePoint images for category:', category, error)
    }

    return unifiedImages
  }

  // Get all images from both sources
  async getAllImages(): Promise<UnifiedImageFile[]> {
    const unifiedImages: UnifiedImageFile[] = []

    // Get all local images
    const localImages = imageManager.getAllImages()
    localImages.forEach(img => {
      if (img.dataUrl) {
        unifiedImages.push({
          id: img.id,
          name: img.name,
          dataUrl: img.dataUrl,
          source: 'local',
          category: img.category
        })
      }
    })

    // Get all SharePoint images
    const sharePointImages = sharepointImageService.getSelectedImagesForWebsite()
    sharePointImages.forEach(img => {
      if (img.localDataUrl) {
        unifiedImages.push({
          id: img.id,
          name: img.name,
          dataUrl: img.localDataUrl,
          source: 'sharepoint',
          category: img.category
        })
      }
    })

    return unifiedImages
  }

  // Check if image exists and is available
  async isImageAvailable(imageId: string): Promise<boolean> {
    const image = await this.getImageById(imageId)
    return image !== null
  }

  // Subscribe to image changes (combines both services)
  subscribe(callback: () => void): () => void {
    const localUnsubscribe = imageManager.subscribe(callback)
    const sharePointUnsubscribe = sharepointImageService.subscribe(callback)
    
    return () => {
      localUnsubscribe()
      sharePointUnsubscribe()
    }
  }

  // Get fallback image for missing images
  getFallbackImage(category: string): UnifiedImageFile {
    const fallbackEmojis = {
      classes: '🎨',
      kits: '📦',
      gallery: '🖼️'
    }

    return {
      id: 'fallback',
      name: 'No image available',
      dataUrl: '', // This will trigger the emoji fallback in components
      source: 'local',
      category
    }
  }
}

export const unifiedImageService = new UnifiedImageService()